import { ErrorComponent } from "@components/ErrorComponent";
import { ErrorDisclaimer } from "@components/ErrorDisclaimer";
import { SelectionInput } from "@components/Input";
import { Loader } from "@components/Loader";
import { OnboardingLayoutProps } from "@components/OnboardingLayout";
import { Spacing, SpacingSizes } from "@components/Spacing";
import { Body } from "@components/Typography";
import { DEFAULT_COUNTRY } from "@constants";
import { UserResidenceStatus } from "@ctypes";
import { EntryStepLayout as OnboardingLayout } from "@entry/EntryStepLayout";
import { useCountries, useErrorBanner, useOnboarding, useSetUserIdentity } from "@hooks";
import { Translations, i18n } from "@i18n";
import { useState } from "react";
import { ResidentSelection } from "./ResidentSelection";
import { styles } from "./styles";
import { CitizenshipRouteComponentProps } from "./type";
import { ResidencyType } from "./types";

export const CitizenshipRoute = () => {
  const { data, loading, error } = useCountries();

  if (loading) return <Loader />;
  if (error || !data) return <ErrorComponent />;

  return <CitizenshipRouteComponent data={data} />;
};

const CitizenshipRouteComponent = ({ data }: CitizenshipRouteComponentProps) => {
  const { showErrorBanner } = useErrorBanner();

  const countryValues = (() => {
    if (!data?.countries) return [];

    return Object.values(data.countries);
  })() as Array<string>;

  const countryKeys = (() => {
    if (!data?.countries) return [];

    return Object.keys(data.countries);
  })() as Array<string>;

  const defaultCountryIndex = countryValues.findIndex((c) => c === DEFAULT_COUNTRY);

  const options = [
    i18n.t(Translations.CitizenshipRouteResidenceStatusCitizen),
    i18n.t(Translations.CitizenshipRouteResidenceStatusResident),
    i18n.t(Translations.CitizenshipRouteResidenceStatusNonResident),
    i18n.t(Translations.CitizenshipRouteResidenceStatusNone),
  ];

  const { resetToNext } = useOnboarding();

  const [countryOfBirthIndex, setCountryOfBirthIndex] = useState<number>(defaultCountryIndex);
  const [countryOfTaxResidenceIndex, setCountryOfTaxResidenceIndex] =
    useState<number>(defaultCountryIndex);
  const [residencyStatusIndex, setResidencyStatusIndex] = useState(0);
  const [status, setStatus] = useState<UserResidenceStatus>(UserResidenceStatus.Citizen);
  const [residencyType, setResidencyType] = useState<ResidencyType>(ResidencyType.Resident);

  const [setUserIdentity, { loading }] = useSetUserIdentity();

  const isCitizen = status === UserResidenceStatus.Citizen;
  const isResident = status === UserResidenceStatus.Resident;

  const isCountryOfTaxResidenceUS =
    countryOfTaxResidenceIndex !== -1 &&
    countryOfTaxResidenceIndex < countryValues.length &&
    countryValues[countryOfTaxResidenceIndex] === DEFAULT_COUNTRY;

  const isValid =
    isCountryOfTaxResidenceUS &&
    (isCitizen || (isResident && residencyType === ResidencyType.Resident));

  const onChangeStatusSelection = (value: string) => {
    const index = options.findIndex((o) => o === value);
    setResidencyStatusIndex(index);

    switch (value) {
      case i18n.t(Translations.CitizenshipRouteResidenceStatusCitizen):
        setStatus(UserResidenceStatus.Citizen);
        break;
      case i18n.t(Translations.CitizenshipRouteResidenceStatusResident):
        setStatus(UserResidenceStatus.Resident);
        break;
      case i18n.t(Translations.CitizenshipRouteResidenceStatusNonResident):
        setStatus(UserResidenceStatus.NonResident);
        break;
      case i18n.t(Translations.CitizenshipRouteResidenceStatusNone):
        setStatus(UserResidenceStatus.None);
        break;
    }
  };

  const onChangeCountryOfBirthSelection = (value: string) => {
    const index = countryValues.findIndex((c) => c === value);
    setCountryOfBirthIndex(index);
  };

  const onChangeCountryOfTaxResidenceSelection = (value: string) => {
    const index = countryValues.findIndex((c) => c === value);
    setCountryOfTaxResidenceIndex(index);
  };

  const handleSubmit = async () => {
    try {
      await setUserIdentity({
        variables: {
          countryOfBirth: countryKeys[countryOfBirthIndex],
          countryOfCitizenship: countryKeys[countryOfTaxResidenceIndex],
          residenceStatus: status,
        },
      });

      resetToNext();
    } catch {
      showErrorBanner();
    }
  };

  const renderError = () => {
    if (!isCountryOfTaxResidenceUS) {
      return (
        <>
          <ErrorDisclaimer message={i18n.t(Translations.CitizenshipRouteRestrictionsResident)} />
          <Spacing size={SpacingSizes.M} />
        </>
      );
    }

    if (!isCitizen && !isResident) {
      return (
        <>
          <ErrorDisclaimer message={i18n.t(Translations.CitizenshipRouteRestrictionsResident)} />
          <Spacing size={SpacingSizes.M} />
        </>
      );
    }

    if (isResident && residencyType === ResidencyType.Visa) {
      return (
        <>
          <ErrorDisclaimer
            message={i18n.t(Translations.CitizenshipRouteRestrictionsVisa)}
            style={styles.visaDisclaimer}
          />
          <Spacing size={SpacingSizes.M} />
        </>
      );
    }

    return null;
  };

  const props: OnboardingLayoutProps = {
    title: i18n.t(Translations.CitizenshipRouteTitle),
    cta: {
      primary: {
        title: i18n.t(Translations.CitizenshipRoutePrimaryCTA),
        loading,
        disabled: !isValid,
        onClick: handleSubmit,
      },
    },
  };

  return (
    <OnboardingLayout {...props}>
      <Spacing size={SpacingSizes.M} />

      <SelectionInput
        label={i18n.t(Translations.CitizenshipRouteCountryOfBirthPrompt)}
        items={countryValues}
        setSelectedIndex={setCountryOfBirthIndex}
        onChangeSelection={onChangeCountryOfBirthSelection}
        defaultIndex={defaultCountryIndex}
      />
      <Spacing size={SpacingSizes.M} />

      <SelectionInput
        label={i18n.t(Translations.CitizenshipRouteCountryOfTaxResidencePrompt)}
        items={countryValues}
        setSelectedIndex={setCountryOfTaxResidenceIndex}
        onChangeSelection={onChangeCountryOfTaxResidenceSelection}
        defaultIndex={defaultCountryIndex}
      />
      <Spacing size={SpacingSizes.M} />

      {isCountryOfTaxResidenceUS ? (
        <>
          <SelectionInput
            label={i18n.t(Translations.CitizenshipRouteResidencyStatusPrompt)}
            items={options}
            setSelectedIndex={setResidencyStatusIndex}
            onChangeSelection={onChangeStatusSelection}
            defaultIndex={residencyStatusIndex}
          />
          <Spacing size={SpacingSizes.M} />

          {isResident ? (
            <>
              <Body style={styles.title}>{i18n.t(Translations.CitizenshipRouteResidencyType)}</Body>
              <Spacing size={SpacingSizes.L} />

              <ResidentSelection
                label={i18n.t(Translations.CitizenshipRouteResidencyTypeResident)}
                value={residencyType === ResidencyType.Resident}
                onClick={() => {
                  setResidencyType(ResidencyType.Resident);
                }}
              />
              <Spacing size={SpacingSizes.M} />

              <ResidentSelection
                label={i18n.t(Translations.CitizenshipRouteResidencyTypeVisa)}
                value={residencyType === ResidencyType.Visa}
                onClick={() => {
                  setResidencyType(ResidencyType.Visa);
                }}
              />
              <Spacing size={SpacingSizes.M} />
            </>
          ) : null}
        </>
      ) : null}

      {renderError()}
    </OnboardingLayout>
  );
};
